import { ForwardedRef, forwardRef, ReactNode, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { ReactSVG } from 'react-svg';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import Link from '@/components/Link';
import analytics from '@/helpers/analytics';
import { SubmenuBlock, SubmenuButtons, SubmenuColumns, SubmenuList } from '@/components/Header/Submenu';

import { headerAtom } from '@/store/common';

import styles from './side-menu-items.module.css';

interface ButtonElementProps {
  href?: string,
  className?: string,
  onClick: () => void,
  children: ReactNode,
}

const ButtonElement = forwardRef(({ href, className, onClick, children }: ButtonElementProps, ref: ForwardedRef<HTMLElement>) => {
  if (href) return (
    <Link
      href={href}
      className={className}
      onClick={onClick}
      ref={ref}
    >
      {children}
    </Link>
  );

  return (
    <div
      className={className}
      onClick={onClick}
      ref={ref as ForwardedRef<HTMLDivElement>}
    >
      {children}
    </div>
  );
});

ButtonElement.displayName = 'ButtonElement';

interface SideMenuItemProps {
  name: string,
  href?: string,
  analyticsId?: string,
  onClick?: () => void,
  children?: ReactNode,
}

const SideMenuItem = ({ name, href, analyticsId, onClick, children }: SideMenuItemProps) => {
  const { t } = useTranslation();
  const { pathname } = useRouter();
  const [isOpen, setOpen] = useState(false);

  const isActive = href && (pathname.startsWith(href) || pathname.startsWith('/[locale]' + href));

  return (
    <div className={styles.item}>
      <ButtonElement
        href={href}
        className={classNames(
          styles.link,
          isOpen && styles.open,
          isActive && styles.active
        )}
        onClick={() => {
          if (!children && onClick) onClick();
          if (analyticsId) analytics.trackYandex(analyticsId);
          setOpen(prev => !prev);
        }}
      >
        {t(name)}
        {!!children && (
          <ReactSVG src="/icons/chevron-down.svg" className={styles.linkArrow} wrapper="span" />
        )}
      </ButtonElement>
      {children && isOpen && (
        <div className={styles.children}>
          {children}
        </div>
      )}
    </div>
  );
};

interface SideMenuItemsProps {
  className?: string,
  onClickItem?: () => void,
}

const SideMenuItems = ({ className, onClickItem }: SideMenuItemsProps) => {
  const { solutions, templates, categories, integrations, useful } = useAtomValue(headerAtom);

  return (
    <nav className={classNames(styles.items, className)}>
      <SideMenuItem
        name="constructor"
      >
        <div className={styles.grid}>
          <SubmenuBlock title="Для кого" className={styles.solutions}>
            <SubmenuList items={solutions} onClickItem={onClickItem} />
          </SubmenuBlock>
          <SubmenuBlock title="Подпись" className={styles.sign} href="/sign" onClick={onClickItem}>
            <span className={styles.signText}>Подписание документов с помощью ПЭП и УКЭП для бизнеса</span>
            <ReactSVG src="/images/menu-stroke.svg" className={styles.signBg} />
          </SubmenuBlock>
          <SubmenuBlock title="Готовые шаблоны" className={styles.templates}>
            <SubmenuColumns templates={templates} categories={categories} onClickItem={onClickItem} />
          </SubmenuBlock>
        </div>
      </SideMenuItem>
      <SideMenuItem
        name="tariffs"
        href="/tariffs"
        analyticsId="tarif_header"
        onClick={onClickItem}
      />
      <SideMenuItem
        name="integrations"
      >
        <SubmenuButtons
          items={[
            ...integrations.map((item) => ({ ...item, type: 'image' as const })),
            {
              id: 'integrations-all',
              name: <span className={styles.integrationsAll}>Смотреть все виджеты <ReactSVG src="/icons/chevron-right.svg" wrapper="span" /></span>,
              href: '/marketplace',
            }
          ]}
            onClickItem={onClickItem}
        />
      </SideMenuItem>
      <SideMenuItem
        name="useful"
      >
        <SubmenuButtons items={useful} onClickItem={onClickItem} />
      </SideMenuItem>
    </nav>
  );
};

export default SideMenuItems;
